import {
  ArrowRight,
  Ban,
  Camera,
  Check,
  Gift,
  Map,
  MessageCircle,
  Music,
  Pause,
  Phone,
  Play,
  Pointer,
  ScanLine,
  ShoppingBag,
  Smartphone,
  Square,
  Volume2,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { Container } from '@/components/ui/Container';
import { QUICK_INSTALL_SECTION } from '@/data/homeContent';
import { FakeQr } from '@/features/marketing/components/shareLinks/FakeQr';
import { fixedImageSizes, imageProps } from '@/lib/imageRenditions';

/** How long each step holds the stage before the story moves on. */
const STEP_MS = 3600;

/**
 * Each step's glyph and colour. The colour is the step's identity: the card,
 * its number, its progress bar and the ring around the phone all wear it, so
 * someone who cannot read "Tap" still sees that the orange card is the one the
 * phone is acting out.
 */
const STEP_LOOKS: { icon: LucideIcon; tile: string; ring: string; bar: string; chip: string }[] = [
  {
    icon: ScanLine,
    tile: 'from-sky-400 to-sky-600',
    ring: 'ring-sky-400/80',
    bar: 'bg-sky-400',
    chip: 'bg-sky-400 text-ink',
  },
  {
    icon: Pointer,
    tile: 'from-saffron-500 to-saffron-600',
    ring: 'ring-saffron-500/80',
    bar: 'bg-saffron-500',
    chip: 'bg-saffron-500 text-ink',
  },
  {
    icon: ShoppingBag,
    tile: 'from-brand-400 to-brand-600',
    ring: 'ring-brand-400/80',
    bar: 'bg-brand-400',
    chip: 'bg-brand-400 text-ink',
  },
];

const PERK_ICONS: LucideIcon[] = [Ban, Smartphone, Gift];

/** The everyday apps already on the drawn home screen - glyph, tint, name -
    so the shop's own icon reads as one app among the ones a shopper knows. */
const HOME_SCREEN_APPS: { icon: LucideIcon; tint: string; name: string }[] = [
  { icon: Phone, tint: 'bg-emerald-500', name: 'Phone' },
  { icon: MessageCircle, tint: 'bg-sky-500', name: 'Messages' },
  { icon: Camera, tint: 'bg-slate-700', name: 'Camera' },
  { icon: Map, tint: 'bg-amber-400', name: 'Maps' },
  { icon: Play, tint: 'bg-rose-500', name: 'Videos' },
  { icon: Music, tint: 'bg-violet-500', name: 'Music' },
];

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function canSpeak() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
}

/**
 * "QuickInstall": the storefront going onto a shopper's phone like an app,
 * told as a three-picture story for a merchant who may not read at all.
 *
 * - Three big step cards, each with one word, a big number and its own colour.
 *   The story plays by itself - the active card fills its progress bar, then
 *   the next one lights up - and any card can be tapped to jump to it.
 * - A phone beside them acts the active step out: the camera finding the QR,
 *   a finger on the Install button, the shop sitting on the home screen next
 *   to WhatsApp. The phone's ring wears the active card's colour.
 * - "Listen" reads the whole thing aloud with the browser's own voice, moving
 *   the story along with the words. It only appears where the browser can talk.
 *
 * Autoplay stops for good the moment someone picks a step, presses pause or
 * presses Listen, and never starts under reduced motion. The code on the card is `FakeQr`, the share-links page's
 * non-scanning pattern, so a camera pointed at the home page goes nowhere.
 */
export function QuickInstall() {
  const { steps, demoShop } = QUICK_INSTALL_SECTION;

  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const speakingRef = useRef(false);

  // Decided after mount, so the server-free first render and the test DOM
  // agree, and the band is still on reduced-motion machines.
  useEffect(() => {
    setPlaying(!prefersReducedMotion());
    setVoiceReady(canSpeak());
  }, []);

  // A timeout keyed on the active step, so jumping to a step gives it a full
  // turn and the progress bar (keyed the same way) restarts in step with it.
  useEffect(() => {
    if (!playing || speaking) return;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % steps.length), STEP_MS);
    return () => window.clearTimeout(id);
  }, [active, playing, speaking, steps.length]);

  const stopSpeaking = useCallback(() => {
    if (!speakingRef.current) return;
    speakingRef.current = false;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, []);

  useEffect(() => stopSpeaking, [stopSpeaking]);

  const pick = (index: number) => {
    stopSpeaking();
    setPlaying(false);
    setActive(index);
  };

  const listen = () => {
    if (speakingRef.current) {
      stopSpeaking();
      return;
    }
    const synth = window.speechSynthesis;
    synth.cancel();
    const lang = document.documentElement.lang || 'en';
    const line = (text: string, onstart?: () => void) => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang;
      u.rate = 0.9;
      if (onstart) u.onstart = onstart;
      return u;
    };
    const lines = [
      line(QUICK_INSTALL_SECTION.sayIntro, () => setActive(0)),
      ...steps.map((step, i) => line(step.say, () => setActive(i))),
      line(QUICK_INSTALL_SECTION.sayOutro),
    ];
    const finish = () => {
      speakingRef.current = false;
      setSpeaking(false);
    };
    lines[lines.length - 1].onend = finish;
    lines.forEach((u) => (u.onerror = finish));

    speakingRef.current = true;
    setSpeaking(true);
    setPlaying(false);
    lines.forEach((u) => synth.speak(u));
  };

  const look = STEP_LOOKS[active % STEP_LOOKS.length];
  const showBar = playing && !speaking;

  return (
    <section aria-labelledby="home-quick-install" className="pb-8 sm:pb-10">
      <Container>
        <div className="relative isolate overflow-hidden rounded-3xl bg-linear-to-br from-brand-900 via-brand-cta to-brand-800 px-5 py-6 text-white shadow-lift sm:px-8 sm:py-8">
          {/* Background: two soft glows and a faint dot grid. */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -start-24 -top-24 size-72 rounded-full bg-brand-400/25 blur-3xl" />
            <div className="absolute -bottom-32 end-0 size-80 rounded-full bg-saffron-500/15 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(rgb(255_255_255/0.07)_1px,transparent_1px)] [background-size:18px_18px]" />
          </div>

          {/* Three blocks: the story, the phone, the buttons. On a phone they
              stack in that order, so the phone sits right under the cards it
              acts out; from `lg` the phone takes the whole right column. */}
          <div className="grid grid-cols-[minmax(0,1fr)] items-center gap-x-10 gap-y-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-y-5">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="inline-flex items-center gap-1.5 rounded-full border border-brand-300/30 bg-white/10 px-3 py-1 text-xs font-bold tracking-wide text-brand-200">
                  <Zap className="size-3.5 text-saffron-500" aria-hidden />
                  {QUICK_INSTALL_SECTION.eyebrow}
                </p>
                {voiceReady ? (
                  <button
                    type="button"
                    onClick={listen}
                    aria-pressed={speaking}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                      speaking
                        ? 'bg-saffron-500 text-ink'
                        : 'border border-saffron-500/60 bg-saffron-500/15 text-saffron-100 hover:bg-saffron-500/25'
                    }`}
                  >
                    {speaking ? (
                      <Square className="size-3 fill-current" aria-hidden />
                    ) : (
                      <Volume2 className="size-3.5" aria-hidden />
                    )}
                    {speaking ? QUICK_INSTALL_SECTION.stopListeningLabel : QUICK_INSTALL_SECTION.listenLabel}
                    {speaking ? <SoundBars /> : null}
                  </button>
                ) : null}
              </div>

              <h2
                id="home-quick-install"
                className="mt-3 text-balance text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl"
              >
                {QUICK_INSTALL_SECTION.heading}
                <span className="block bg-linear-to-r from-brand-300 via-brand-200 to-saffron-100 bg-clip-text text-transparent">
                  {QUICK_INSTALL_SECTION.headingAccent}
                </span>
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-white/80 sm:text-base">
                {QUICK_INSTALL_SECTION.subheading}
              </p>

              {/* The story: three picture cards, the active one lit in its colour. */}
              <ol className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
                {steps.map((step, index) => {
                  const { icon: Icon, tile, bar, chip } = STEP_LOOKS[index % STEP_LOOKS.length];
                  const isActive = index === active;
                  const isDone = index < active;
                  return (
                    <li
                      key={step.title}
                      aria-current={isActive ? 'step' : undefined}
                      className={`relative flex flex-col items-center overflow-hidden rounded-2xl border px-2 pb-3 pt-3 text-center transition-all duration-300 focus-within:ring-2 focus-within:ring-white/70 sm:px-3 ${
                        isActive
                          ? 'scale-[1.03] border-white/30 bg-white/15 shadow-xl shadow-black/30'
                          : 'border-white/10 bg-white/5 opacity-75 hover:opacity-100'
                      }`}
                    >
                      <span
                        className={`relative grid size-14 place-items-center rounded-2xl bg-linear-to-br ${tile} shadow-lg shadow-black/30 sm:size-16`}
                      >
                        <Icon
                          className={`size-7 text-white sm:size-8 ${isActive ? 'motion-safe:animate-[quick-install-float_1.6s_ease-in-out_infinite]' : ''}`}
                          aria-hidden
                        />
                        <span
                          className={`absolute -end-2 -top-2 grid size-7 place-items-center rounded-full text-sm font-black ring-2 ring-brand-900 ${chip}`}
                          aria-hidden
                        >
                          {isDone ? <Check className="size-4" strokeWidth={3} /> : index + 1}
                        </span>
                      </span>
                      <h3 className="mt-2 text-base font-extrabold tracking-tight sm:text-lg">
                        {/* The whole card is the button: its ::after stretches over the card. */}
                        <button
                          type="button"
                          onClick={() => pick(index)}
                          className="after:absolute after:inset-0 after:content-[''] focus:outline-none"
                        >
                          <span className="sr-only">{index + 1}. </span>
                          {step.title}
                        </button>
                      </h3>
                      <p className="text-[11px] leading-snug text-white/70 sm:text-xs">{step.description}</p>

                      {/* How long this step has left on stage. */}
                      <span className="absolute inset-x-0 bottom-0 h-1 bg-white/10" aria-hidden>
                        {isActive ? (
                          <span
                            key={`${active}-${showBar}`}
                            className={`block h-full ${bar} ${
                              showBar
                                ? 'w-0 motion-safe:animate-[quick-install-progress_linear_forwards]'
                                : 'w-full'
                            }`}
                            style={showBar ? { animationDuration: `${STEP_MS}ms` } : undefined}
                          />
                        ) : isDone ? (
                          <span className={`block h-full w-full ${bar} opacity-40`} />
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <button
                  type="button"
                  onClick={() => {
                    stopSpeaking();
                    setPlaying((p) => !p);
                  }}
                  aria-label={playing ? QUICK_INSTALL_SECTION.pauseLabel : QUICK_INSTALL_SECTION.playLabel}
                  className="grid size-7 place-items-center rounded-full border border-white/25 text-white/80 transition-colors hover:bg-white/10"
                >
                  {playing ? (
                    <Pause className="size-3.5 fill-current" aria-hidden />
                  ) : (
                    <Play className="size-3.5 fill-current" aria-hidden />
                  )}
                </button>
                <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                  {QUICK_INSTALL_SECTION.perks.map((perk, index) => {
                    const PerkIcon = PERK_ICONS[index % PERK_ICONS.length];
                    return (
                      <li key={perk} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/85">
                        <span className="grid size-5 place-items-center rounded-full bg-white/10">
                          <PerkIcon className="size-3 text-brand-200" aria-hidden />
                        </span>
                        {perk}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="order-last lg:order-none lg:col-start-1">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Link
                  to={QUICK_INSTALL_SECTION.cta.to}
                  className="group inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-brand-700 shadow-lg shadow-brand-900/30 transition-all hover:-translate-y-0.5 hover:bg-brand-50 sm:px-5"
                >
                  {QUICK_INSTALL_SECTION.cta.label}
                  <ArrowRight
                    className="size-4 transition-transform group-hover:translate-x-0.5 rtl-flip"
                    aria-hidden
                  />
                </Link>
                <Link
                  to={QUICK_INSTALL_SECTION.secondaryCta.to}
                  className="inline-flex items-center rounded-xl border border-white/25 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:border-white/50 hover:bg-white/10 sm:px-5"
                >
                  {QUICK_INSTALL_SECTION.secondaryCta.label}
                </Link>
              </div>
              <p className="mt-3 text-xs font-medium text-white/60">{QUICK_INSTALL_SECTION.planNote}</p>
            </div>

            {/* The stage: one phone acting out the active step. */}
            <div className="relative flex items-center justify-center pt-2 lg:col-start-2 lg:row-span-2 lg:row-start-1">
              <div className="absolute size-64 rounded-full bg-brand-400/20 blur-3xl sm:size-80" aria-hidden />

              <div className="relative shrink-0">
                <div
                  role="img"
                  aria-label={steps[active].sceneLabel}
                  className={`relative h-80 w-40 overflow-hidden rounded-[2rem] border-[7px] border-ink bg-ink shadow-2xl shadow-black/50 ring-4 ring-offset-4 ring-offset-brand-900 transition-[box-shadow] duration-500 sm:h-96 sm:w-48 ${look.ring}`}
                >
                  <Scene shown={active === 0}>
                    <ScanScene />
                  </Scene>
                  <Scene shown={active === 1}>
                    <TapScene shopName={demoShop.name} logo={demoShop.logo} products={demoShop.products} />
                  </Scene>
                  <Scene shown={active === 2}>
                    <HomeScene shopName={demoShop.name} logo={demoShop.logo} />
                  </Scene>

                  {/* Status bar, over every scene. */}
                  <div className="absolute inset-x-0 top-0 flex items-center justify-between px-3.5 pt-1.5 text-[8px] font-semibold text-white">
                    <span>9:41</span>
                    <span className="h-3.5 w-11 rounded-full bg-ink" />
                    <span>5G</span>
                  </div>
                </div>

                {/* The active step's number and word, pinned to the phone. */}
                <span
                  key={active}
                  className={`absolute end-full top-12 me-2 inline-flex items-center gap-1.5 rounded-full py-1 pe-3 ps-1 text-xs font-extrabold shadow-xl motion-safe:animate-[fade-in_0.4s_ease-out] ${look.chip}`}
                  aria-hidden
                >
                  <span className="grid size-6 place-items-center rounded-full bg-white/90 text-ink">{active + 1}</span>
                  {steps[active].title}
                </span>

                {/* One floating badge, centred over the top of the phone by a
                    full-width row rather than a translate, so it needs no
                    flipping for right-to-left. */}
                <span className="pointer-events-none absolute inset-x-0 -top-4 flex justify-center" aria-hidden>
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-brand-700 shadow-xl motion-safe:animate-[quick-install-float_4s_ease-in-out_infinite]">
                    <Zap className="size-3 text-saffron-500" aria-hidden />
                    {QUICK_INSTALL_SECTION.badge}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

/** One of the phone's three screens, cross-faded in when its step is active. */
function Scene({ shown, children }: { shown: boolean; children: ReactNode }) {
  return (
    <div
      aria-hidden={!shown}
      className={`absolute inset-0 transition-all duration-500 motion-reduce:transition-none ${
        shown ? 'scale-100 opacity-100' : 'pointer-events-none scale-95 opacity-0'
      }`}
    >
      {children}
    </div>
  );
}

/** Step 1: the camera viewfinder framing the counter card's code. */
function ScanScene() {
  return (
    <div className="flex size-full flex-col items-center justify-center bg-linear-to-b from-slate-800 to-slate-950 px-4">
      <p className="mb-3 inline-flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-[8px] font-semibold text-white/90">
        <Camera className="size-2.5" aria-hidden />
        Camera
      </p>
      <div className="relative w-full p-2.5">
        {/* Viewfinder corners. */}
        <span className="absolute start-0 top-0 size-5 rounded-ss-lg border-s-[3px] border-t-[3px] border-sky-400" />
        <span className="absolute end-0 top-0 size-5 rounded-se-lg border-e-[3px] border-t-[3px] border-sky-400" />
        <span className="absolute bottom-0 start-0 size-5 rounded-es-lg border-b-[3px] border-s-[3px] border-sky-400" />
        <span className="absolute bottom-0 end-0 size-5 rounded-ee-lg border-b-[3px] border-e-[3px] border-sky-400" />
        <div className="relative rotate-[-4deg] rounded-xl bg-white p-2 text-ink shadow-xl">
          <p className="text-center text-[8px] font-extrabold uppercase tracking-wider text-brand-600">Scan me</p>
          <div className="relative mt-1">
            <FakeQr seed="home-quick-install" />
            <span className="absolute inset-x-0 top-[6%] h-0.5 rounded-full bg-sky-500 shadow-[0_0_12px_3px_rgb(14_165_233/0.6)] motion-safe:animate-[quick-install-scan_2.2s_ease-in-out_infinite]" />
          </div>
        </div>
      </div>
      <span className="mt-6 grid size-10 place-items-center rounded-full border-[3px] border-white/80">
        <span className="size-7 rounded-full bg-white" />
      </span>
    </div>
  );
}

/** Step 2: the shop page with the install sheet up and a finger on Install. */
function TapScene({
  shopName,
  logo,
  products,
}: {
  shopName: string;
  logo: string;
  products: { name: string; price: string; image: string }[];
}) {
  return (
    <div className="relative flex size-full flex-col bg-white pt-6 text-ink">
      {/* The shop page, dimmed behind the sheet. */}
      <div className="flex items-center gap-1.5 border-b border-line px-3 py-2">
        <img src={logo} alt="" className="size-5 rounded-full" />
        <span className="truncate text-[9px] font-bold">{shopName}</span>
      </div>
      <div className="grid grid-cols-2 gap-1.5 p-2.5">
        {products.map((product) => (
          <span key={product.name} className="flex min-w-0 flex-col overflow-hidden rounded-lg bg-white ring-1 ring-line">
            <img
              {...imageProps(product.image, fixedImageSizes(80))}
              alt=""
              loading="lazy"
              className="aspect-square w-full bg-saffron-50 object-cover"
            />
            <span className="truncate px-1 pt-0.5 text-[6.5px] font-semibold">{product.name}</span>
            <span className="px-1 pb-1 text-[7px] font-extrabold text-brand-700">{product.price}</span>
          </span>
        ))}
      </div>
      <div className="absolute inset-0 bg-black/40" />

      {/* The install sheet. */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center rounded-t-2xl bg-white px-3 pb-4 pt-2 shadow-2xl">
        <span className="mb-2 h-1 w-8 rounded-full bg-line" />
        <span className="grid size-11 place-items-center overflow-hidden rounded-xl shadow-md ring-1 ring-line">
          <img src={logo} alt="" className="size-full object-cover" />
        </span>
        <p className="mt-1.5 truncate text-[10px] font-extrabold">{shopName}</p>
        <div className="relative mt-2.5 w-full">
          <span className="block w-full rounded-xl bg-brand-500 py-2 text-center text-[11px] font-extrabold text-white shadow-lg shadow-brand-500/40">
            Install
          </span>
          <span className="absolute inset-0 rounded-xl ring-4 ring-saffron-500/70 motion-safe:animate-ping" />
          {/* The finger, pressing. */}
          <Pointer
            className="absolute -bottom-4 end-5 size-9 fill-white stroke-ink drop-shadow-lg motion-safe:animate-[quick-install-tap_1.2s_ease-in-out_infinite]"
            strokeWidth={1.5}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

/** Step 3: the home screen, the shop's icon new among the everyday apps. */
function HomeScene({ shopName, logo }: { shopName: string; logo: string }) {
  return (
    <div className="relative size-full bg-linear-to-b from-indigo-400 via-sky-300 to-brand-200 pt-7">
      <div className="grid grid-cols-4 gap-x-2 gap-y-3 px-3 pt-3">
        <PhoneApp name="WhatsApp">
          <img src="/icons/whatsapp.png" alt="" className="size-full rounded-[0.55rem] object-cover" />
        </PhoneApp>
        {HOME_SCREEN_APPS.map(({ icon: Icon, tint, name }) => (
          <PhoneApp key={name} name={name}>
            <span className={`grid size-full place-items-center rounded-[0.55rem] ${tint} shadow-sm`}>
              <Icon className="size-3/5 text-white" aria-hidden />
            </span>
          </PhoneApp>
        ))}
        <PhoneApp name={shopName} highlight>
          <span className="absolute -inset-1 rounded-[0.7rem] bg-saffron-500/70 motion-safe:animate-ping" />
          <span className="relative grid size-full place-items-center overflow-hidden rounded-[0.55rem] bg-white shadow-md ring-2 ring-saffron-500">
            <img src={logo} alt="" className="size-full object-cover" />
          </span>
        </PhoneApp>
      </div>

      {/* The "ready" toast at the bottom. */}
      <div className="absolute inset-x-2 bottom-2 flex items-center gap-1.5 rounded-xl bg-white p-2 shadow-xl">
        <span className="grid size-7 shrink-0 place-items-center overflow-hidden rounded-md ring-1 ring-line">
          <img src={logo} alt="" className="size-full object-cover" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[8px] font-bold text-ink">{shopName}</p>
          <p className="truncate text-[7px] font-semibold text-brand-600">App ready</p>
        </div>
        <span className="grid size-5 place-items-center rounded-full bg-brand-500">
          <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
        </span>
      </div>
    </div>
  );
}

/** One icon on the drawn home screen, with its name under it. */
function PhoneApp({
  name,
  highlight = false,
  children,
}: {
  name: string;
  highlight?: boolean;
  children: ReactNode;
}) {
  return (
    <span className="flex min-w-0 flex-col items-center gap-0.5">
      <span className="relative aspect-square w-full">{children}</span>
      <span
        className={`w-full truncate text-center text-[6px] font-semibold ${highlight ? 'font-extrabold text-ink' : 'text-white'}`}
      >
        {name}
      </span>
    </span>
  );
}

/** Three little bars bouncing while the voice is talking. */
function SoundBars() {
  return (
    <span className="ms-0.5 inline-flex h-3 items-end gap-px" aria-hidden>
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="w-0.5 origin-bottom rounded-full bg-current motion-safe:animate-[quick-install-bars_0.9s_ease-in-out_infinite]"
          style={{ animationDelay: `${delay}ms`, height: '100%' }}
        />
      ))}
    </span>
  );
}
